import { lazy, Suspense, type ReactNode } from "react";
import { Navigate, Outlet, type RouteObject, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ErrorBoundary } from "@sentry/react";
import { ErrorFallback } from "@/app/error-boundaries/ErrorFallback";
import { RouteErrorFallback } from "@/app/error-boundaries/RouteErrorFallback";
import { ProtectedRoute, PublicRoute, AdminRedirect, InvestorRedirect } from "@/app/guards";
import { ROUTES } from "@/app/routes";
import { LoadingScreen } from "@/app/fallbacks/LoadingScreen";
import { legacyShellRoutes } from "@/app/legacyShellRoutes";
import { ShellRoute } from "@/app/ShellRoute";
import { useOrganizationSetup } from "@/app/useOrganizationSetup";
import { PanelPlaceholder } from "@/components/shared/layout/PanelPlaceholder";
import { SettingsShell } from "@/presentation/shell/SettingsShell";
import { SETTINGS_ROUTES } from "@/presentation/shell/settingsNavigation";
import { SHELL_ROUTES } from "@/presentation/shell/shellNavigation";

const LoginPage = lazy(() => import("@/presentation/pages/auth/LoginPage"));
const ResetPasswordPage = lazy(() => import("@/presentation/pages/auth/ResetPasswordPage"));
const VerifyEmailPage = lazy(() => import("@/presentation/pages/auth/VerifyEmailPage"));
const FoundationPage = lazy(() => import("@/presentation/pages/foundation/FoundationPage"));
const ShellPendingPage = lazy(() => import("@/presentation/shell/ShellPendingPage"));
const DashboardPage = lazy(() => import("@/presentation/pages/dashboard/DashboardPage"));
const ActivityPage = lazy(() => import("@/presentation/pages/activity/ActivityPage"));
const OrganizationActivityPage = lazy(
  () => import("@/presentation/pages/settings/OrganizationActivityPage"),
);
const TasksPage = lazy(() => import("@/presentation/pages/tasks/TasksPage"));
const BeneficiariesPage = lazy(
  () => import("@/presentation/pages/beneficiaries/BeneficiariesPage"),
);
const BeneficiaryDetailPage = lazy(
  () => import("@/presentation/pages/beneficiaries/BeneficiaryDetailPage"),
);
const AuditDetailPage = lazy(() => import("@/presentation/pages/beneficiaries/AuditDetailPage"));
const QuotaDetailPage = lazy(() => import("@/presentation/pages/beneficiaries/QuotaDetailPage"));
const QuotasPage = lazy(() => import("@/presentation/pages/quotas/QuotasPage"));
const CalculatorPage = lazy(() => import("@/presentation/pages/calculator/CalculatorPage"));
const InvestorsPage = lazy(() => import("@/presentation/pages/investors/InvestorsPage"));
const InvestorDetailPage = lazy(() => import("@/presentation/pages/investors/InvestorDetailPage"));
const ProposalDetailPage = lazy(() => import("@/presentation/pages/investors/ProposalDetailPage"));
const TemplatesPage = lazy(() => import("@/presentation/pages/templates/TemplatesPage"));
const TemplateEditorPage = lazy(() => import("@/presentation/pages/templates/TemplateEditorPage"));
const ContractsPage = lazy(() => import("@/presentation/pages/contracts/ContractsPage"));
const ContractDetailPage = lazy(() => import("@/presentation/pages/contracts/ContractDetailPage"));
const InvestmentsPage = lazy(() => import("@/presentation/pages/investments/InvestmentsPage"));
const InvestmentDetailPage = lazy(
  () => import("@/presentation/pages/investments/InvestmentDetailPage"),
);
const PublicProposalPage = lazy(() => import("@/presentation/pages/proposals/PublicProposalPage"));
const AcceptInvitationPage = lazy(
  () => import("@/presentation/pages/invitations/AcceptInvitationPage"),
);
const ConfirmEmailChangePage = lazy(
  () => import("@/presentation/pages/auth/ConfirmEmailChangePage"),
);
const ProfilePage = lazy(() => import("@/presentation/pages/settings/ProfilePage"));
const PreferencesPage = lazy(() => import("@/presentation/pages/settings/PreferencesPage"));
const NotificationsPage = lazy(() => import("@/presentation/pages/settings/NotificationsPage"));
const SecurityPage = lazy(() => import("@/presentation/pages/settings/SecurityPage"));
const OrganizationGeneralPage = lazy(
  () => import("@/presentation/pages/settings/OrganizationGeneralPage"),
);
const MembersPage = lazy(() => import("@/presentation/pages/settings/MembersPage"));
const TagsPage = lazy(() => import("@/presentation/pages/settings/TagsPage"));
const MyOrganizationsPage = lazy(() => import("@/presentation/pages/settings/MyOrganizationsPage"));

const SHELL_PAGES: Partial<Record<string, ReactNode>> = {
  [ROUTES.DASHBOARD]: <DashboardPage />,
  [ROUTES.ACTIVITY]: <ActivityPage />,
  [ROUTES.TASKS]: <TasksPage />,
  [ROUTES.BENEFICIARIES.LIST]: <BeneficiariesPage />,
  [ROUTES.BENEFICIARIES.DETAIL(":id")]: <BeneficiaryDetailPage />,
  [ROUTES.BENEFICIARIES.AUDIT(":id", ":quotaId")]: <AuditDetailPage />,
  [ROUTES.QUOTAS]: <QuotasPage />,
  [ROUTES.BENEFICIARIES.QUOTA(":id", ":quotaId")]: <QuotaDetailPage />,
  [ROUTES.BENEFICIARIES.INVESTMENT(":id", ":quotaId", ":investmentId")]: <InvestmentDetailPage />,
  [ROUTES.INVESTMENTS.INVESTORS]: <InvestorsPage />,
  [ROUTES.INVESTMENTS.CALCULATOR]: <CalculatorPage />,
  [ROUTES.INVESTORS.DETAIL(":id")]: <InvestorDetailPage />,
  [ROUTES.ORGANIZATION.TEMPLATES]: <TemplatesPage />,
  [ROUTES.ORGANIZATION.TEMPLATE_DETAIL(":id")]: <TemplateEditorPage />,
  [ROUTES.INVESTMENTS.MAIN]: <InvestmentsPage />,
  [ROUTES.INVESTMENTS.DETAIL(":uuid")]: <InvestmentDetailPage />,
  [ROUTES.INVESTORS.INVESTMENT(":id", ":investmentId")]: <InvestmentDetailPage />,
  [ROUTES.INVESTORS.PROPOSAL(":id", ":proposalId")]: <ProposalDetailPage />,
  [ROUTES.INVESTMENTS.CONTRACTS]: <ContractsPage />,
  [ROUTES.INVESTMENTS.CONTRACT_DETAIL(":uuid")]: <ContractDetailPage />,
};

const SETTINGS_PAGES: Partial<Record<string, ReactNode>> = {
  [ROUTES.ORGANIZATION.ACTIVITY]: <OrganizationActivityPage />,
  [ROUTES.PROFILE]: <ProfilePage />,
  [ROUTES.PREFERENCES]: <PreferencesPage />,
  [ROUTES.NOTIFICATIONS]: <NotificationsPage />,
  [ROUTES.SECURITY]: <SecurityPage />,
  [ROUTES.ORGANIZATION.GENERAL]: <OrganizationGeneralPage />,
  [ROUTES.ORGANIZATION.USERS]: <MembersPage />,
  [ROUTES.ORGANIZATION.TAGS]: <TagsPage />,
  [ROUTES.ORGANIZATION.MEMBERSHIPS]: <MyOrganizationsPage />,
};

function SettingsShellRoute() {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const organizations = useOrganizationSetup(pathname, search, { loadDetails: false });
  return (
    <SettingsShell
      organizations={organizations}
      loadingFallback={
        <PanelPlaceholder
          className="flex min-h-64 flex-col"
          loading
          loadingLabel={t("common.loading")}
        />
      }
    />
  );
}

function RouteContent({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary fallback={ErrorFallback}>
      <Suspense fallback={<LoadingScreen />}>{children}</Suspense>
    </ErrorBoundary>
  );
}

/** Auth is available; E3–E13 replace the remaining pending surfaces. */
export function createAppRoutes(): RouteObject[] {
  const retired = (
    <ProtectedRoute>
      <Navigate to={ROUTES.PORTAL_UNAVAILABLE} replace />
    </ProtectedRoute>
  );
  return [
    {
      element: <Outlet />,
      errorElement: <RouteErrorFallback />,
      children: [
        {
          path: ROUTES.AUTH.LOGIN,
          element: (
            <PublicRoute>
              <RouteContent>
                <LoginPage />
              </RouteContent>
            </PublicRoute>
          ),
        },
        {
          path: ROUTES.AUTH.RESET_PASSWORD,
          element: (
            <PublicRoute>
              <RouteContent>
                <ResetPasswordPage />
              </RouteContent>
            </PublicRoute>
          ),
        },
        {
          path: ROUTES.AUTH.CONFIRM_EMAIL_CHANGE,
          element: (
            <RouteContent>
              <ConfirmEmailChangePage />
            </RouteContent>
          ),
        },
        ...[ROUTES.AUTH.VERIFY_EMAIL, ROUTES.AUTH.VERIFY_EMAIL_LINK].map((path) => ({
          path,
          element: (
            <RouteContent>
              <VerifyEmailPage />
            </RouteContent>
          ),
        })),
        // These links must remain accessible with and without a session.
        {
          path: ROUTES.INVITATIONS.ACCEPT(":uuid", ":invitationId"),
          element: (
            <RouteContent>
              <AcceptInvitationPage />
            </RouteContent>
          ),
        },
        {
          path: ROUTES.PROPOSALS.DETAIL(":uuid"),
          element: (
            <RouteContent>
              <PublicProposalPage />
            </RouteContent>
          ),
        },
        {
          path: ROUTES.PORTAL_UNAVAILABLE,
          element: (
            <ProtectedRoute>
              <RouteContent>
                <FoundationPage kind="portal_unavailable" />
              </RouteContent>
            </ProtectedRoute>
          ),
        },
        { path: `${ROUTES.ADMIN.ROOT}/*`, element: retired },
        { path: `${ROUTES.HUB.ROOT}/*`, element: retired },
        {
          element: (
            <ProtectedRoute>
              <AdminRedirect>
                <InvestorRedirect>
                  <ShellRoute />
                </InvestorRedirect>
              </AdminRedirect>
            </ProtectedRoute>
          ),
          children: [
            ...legacyShellRoutes,
            ...SHELL_ROUTES.map(({ path }) => ({
              path,
              element: SHELL_PAGES[path] ?? <ShellPendingPage />,
            })),
          ],
        },
        {
          element: (
            <ProtectedRoute>
              <AdminRedirect>
                <InvestorRedirect>
                  <SettingsShellRoute />
                </InvestorRedirect>
              </AdminRedirect>
            </ProtectedRoute>
          ),
          children: [
            { path: ROUTES.SETTINGS, element: <Navigate to={ROUTES.PROFILE} replace /> },
            ...SETTINGS_ROUTES.map(({ path }) => ({
              path,
              element: SETTINGS_PAGES[path] ?? <ShellPendingPage />,
            })),
          ],
        },
        {
          path: "*",
          element: (
            <RouteContent>
              <FoundationPage kind="not_found" />
            </RouteContent>
          ),
        },
      ],
    },
  ];
}
